import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useEffect,
  useCallback,
} from "react";
import type { ReactElement, ComponentProps } from "react";

import { Box, Checkbox, HStack, Text, Pressable, Tooltip, Button } from "native-base";

type BoxProps = ComponentProps<typeof Box>;

export interface SelectRowRef {
  getIsChecked: () => boolean;
  setIsChecked: (isChecked: boolean) => void;
}

interface Props extends BoxProps {
  id: string;
  initiallySelected: boolean;
  label: string;
  onPressItem?: (variables: { id: string; label?: string }) => void;
  onSelect: (variables: { id: string; label?: string }) => void;
  renderRightElement?: () => ReactElement;
  tooltipLabel?: string;
}

const SelectRow = forwardRef<SelectRowRef, Props>((props, ref) => {
  const { id, initiallySelected, label, onPressItem, onSelect, renderRightElement, tooltipLabel } =
    props;

  const previousId = useRef(id);
  const currentCheckedRef = useRef(initiallySelected);

  const [checkBoxIsChecked, setCheckBoxIsChecked] = useState(initiallySelected);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    // reset checked state when id changes (as this component is re-used by RLV)
    if (previousId.current !== id) {
      setCheckBoxIsChecked(initiallySelected);
      currentCheckedRef.current = initiallySelected;
      previousId.current = id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useImperativeHandle(ref, () => ({
    getIsChecked: () => currentCheckedRef.current,
    setIsChecked: (isChecked: boolean) => {
      currentCheckedRef.current = isChecked;
      setCheckBoxIsChecked(isChecked);
    },
  }));

  const renderRow = useCallback(() => {
    return (
      <Pressable
        _hover={{ opacity: 0.8 }}
        _pressed={{ opacity: 0.7 }}
        alignItems="center"
        bg="surface.50"
        borderBottomWidth={1}
        borderColor="surface.200"
        flexDirection="row"
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        onPress={() => {
          setCheckBoxIsChecked((checkBoxIsChecked) => {
            currentCheckedRef.current = !checkBoxIsChecked;
            return !checkBoxIsChecked;
          });
          onSelect({ id, label });
        }}
        pr="4"
        py="2"
      >
        <Box my="-3" pl="4" pointerEvents={onPressItem ? "auto" : "none"} pr="3" py="2">
          <Checkbox isChecked={checkBoxIsChecked} pointerEvents="none" size="sm" value={id} />
        </Box>
        <HStack alignItems="center" flex={1} justifyContent="space-between">
          <Text flex={1} fontFamily="Poppins-Regular" fontSize="md" isTruncated numberOfLines={1}>
            {label}
          </Text>
          {isHovered && onPressItem ? (
            <Button
              _hover={{ opacity: 0.8 }}
              _pressed={{ opacity: 0.7 }}
              borderRadius="2xl"
              fontSize="md"
              onHoverIn={() => setIsHovered(true)}
              onPress={() => {
                onPressItem({ id, label });
              }}
              px="2"
              py="0"
            >
              View
            </Button>
          ) : renderRightElement ? (
            renderRightElement()
          ) : null}
        </HStack>
      </Pressable>
    );
  }, [checkBoxIsChecked, id, isHovered, label, onPressItem, onSelect, renderRightElement]);

  if (!tooltipLabel) {
    return renderRow();
  }

  return (
    <Tooltip label={tooltipLabel} placement="bottom" pointerEvents="none" textAlign="center">
      {renderRow()}
    </Tooltip>
  );
});

export default SelectRow;
